import { DocumentModel } from "../types/document";
import { parseTableCellId, scrollToParagraph, scrollToTableCell } from "../../../utils/taskpane";

export interface Citation {
  /** The segment ID referenced by this citation (e.g. "pD" or "t0r1c2") */
  segmentId: string;
  /** Bookmark name created for reliable scroll-to, set after processing */
  bookmarkName?: string;
  /** Short label for the reference footer (first ~60 chars of paragraph) */
  label: string;
}

export interface ParsedResponse {
  /** The assistant's text with <segment=XX> tags replaced by numbered markers like §1, §2 */
  content: string;
  /** Unique citations in order of first appearance */
  citations: Citation[];
}

const SEGMENT_TAG_RE = /<segment=(p[A-Z]+|t\d+r\d+c\d+)>/g;

/**
 * Parses an assistant response containing inline `<segment=XX>` tags.
 *
 * Accepts both paragraph IDs (`pA`, `pAB`, etc.) and table cell IDs (`t0r1c2`).
 * Each unique segment ID is assigned a sequential number (starting from 1).
 * The tags are replaced with Markdown links `[[n]](#cite-n)` so that any
 * Markdown renderer can handle them — we just override the `a` component
 * to intercept fragment URLs.
 *
 * Duplicate references to the same segment reuse the same number.
 */

export const parseCitations = (raw: string): ParsedResponse => {
  const citations: Citation[] = [];
  const seen = new Map<string, number>(); // segmentId → citation index (1-based)

  const content = raw.replace(SEGMENT_TAG_RE, (_match, segmentId: string) => {
    let num = seen.get(segmentId);
    if (num === undefined) {
      num = citations.length + 1;
      seen.set(segmentId, num);
      citations.push({ segmentId, label: segmentId });
    }
    return `[[${num}]](#cite-${num})`;
  });

  return { content: content.trim(), citations };
};

/**
 * Enrich citation labels and create bookmarks so they can navigate into the
 * document even after subsequent edits.
 */
export const enrichCitations = async (citations: Citation[], docModel: DocumentModel) => {
  for (const cit of citations) {
    const cellRef = parseTableCellId(cit.segmentId);

    if (cellRef && docModel.tables) {
      // Table-cell citation (e.g. "t0r1c2")
      const tableModel = docModel.tables.find((t) => t.tableIndex === cellRef.tableIndex);
      if (tableModel) {
        const cell = tableModel.cells.find(
          (c) => c.row === cellRef.row && c.column === cellRef.column
        );
        const headerCell = tableModel.cells.find((c) => c.row === 0 && c.column === cellRef.column);
        const colName = headerCell?.text || `Col ${cellRef.column + 1}`;
        const cellText = cell?.text || "";
        const cellLabel = `Table ${cellRef.tableIndex + 1}, ${colName}: ${cellText}`;
        cit.label = cellLabel.length > 80 ? cellLabel.substring(0, 77) + "..." : cellLabel;
      }
      const scrollResult = await scrollToTableCell(
        cellRef.tableIndex,
        cellRef.row,
        cellRef.column,
        true
      );
      if (scrollResult.success && scrollResult.bookmarkName) {
        cit.bookmarkName = scrollResult.bookmarkName;
      }
    } else {
      // Paragraph citation (e.g. "pF") — possibly a table-level segment
      const segment = docModel.segments.find((s) => s.segmentId === cit.segmentId);
      if (segment) {
        if (segment.type === "table" && segment.table && docModel.tables) {
          const tableModel = docModel.tables.find(
            (t) => t.tableIndex === segment.table!.tableIndex
          );
          if (tableModel) {
            const headerCells = tableModel.cells
              .filter((c) => c.row === 0)
              .sort((a, b) => a.column - b.column)
              .map((c) => c.text);
            const headerPreview = headerCells.join(" | ");
            const tableLabel = `Table ${tableModel.tableIndex + 1}: ${headerPreview}`;
            cit.label = tableLabel.length > 80 ? tableLabel.substring(0, 77) + "..." : tableLabel;
          } else {
            cit.label = `Table ${segment.table.tableIndex + 1}`;
          }
        } else {
          cit.label =
            segment.text.length > 60 ? segment.text.substring(0, 57) + "..." : segment.text;
        }
      }
      const scrollResult = await scrollToParagraph(cit.segmentId, undefined, true);
      if (scrollResult.success && scrollResult.bookmarkName) {
        cit.bookmarkName = scrollResult.bookmarkName;
      }
    }
  }
};
